import React, { useMemo, useState } from 'react';
import { Hash, KeyRound, Loader, Mail, Shield, UserRound } from 'lucide-react';
import api from '../../services/api';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import { notifyError, notifySuccess } from '../../utils/notificationCenter';
import { cn } from '../../utils/cn';
import {
  ApartmentUnitsEditor,
  ApartmentUnitRow,
  parseApartmentUnitsFromProfile,
  serializeApartmentUnits,
  validateApartmentUnits,
} from './ApartmentUnitsEditor';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ResidentialComplex {
  id: string;
  name: string;
}

export interface EditUserModalUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  managedComplexId?: string;
  assignedComplexes?: Array<{ id: string; name: string }>;
  profile?: {
    apartment?: string;
    complex?: string;
    complexId?: string;
    coefficient?: number | string;
    phone?: string;
    document?: string;
  };
}

interface EditUserFormState {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  complex: string;
  complexId: string;
  phone: string;
  document: string;
  managedComplexId: string;
  assignedComplexIds: string[];
  syncPasswordWithDocument: boolean;
  units: ApartmentUnitRow[];
}

interface EditUserModalProps {
  user: EditUserModalUser;
  complexes: ResidentialComplex[];
  onClose: () => void;
  onSaved: () => void;
}

const normalizeComparable = (value: string) => value.trim().toLowerCase();

const unitsSignature = (units: ApartmentUnitRow[]) =>
  JSON.stringify(
    units.map((unit) => ({
      tower: unit.tower.trim(),
      apartment: unit.apartment.trim(),
      coefficient: unit.coefficient.trim(),
    }))
  );

const buildFormFromUser = (user: EditUserModalUser): EditUserFormState => {
  return {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    role: user.role || 'owner',
    isActive: user.isActive,
    complex: user.profile?.complex || '',
    complexId: user.profile?.complexId || '',
    phone: user.profile?.phone || '',
    document: user.profile?.document || '',
    managedComplexId: user.managedComplexId || '',
    assignedComplexIds: (user.assignedComplexes || []).map((item) => item.id),
    syncPasswordWithDocument: false,
    units: parseApartmentUnitsFromProfile(user.profile?.apartment, user.profile?.coefficient),
  };
};

export const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  complexes,
  onClose,
  onSaved,
}) => {
  const { confirm } = useConfirm();
  const [originalForm] = useState(() => buildFormFromUser(user));
  const [form, setForm] = useState<EditUserFormState>(() => buildFormFromUser(user));
  const [saving, setSaving] = useState(false);

  const isResidentialRole = form.role !== 'manager' && form.role !== 'builder';
  const emailFormatError =
    form.email.trim() && !EMAIL_REGEX.test(form.email.trim())
      ? 'Correo electrónico inválido'
      : '';

  const isEmailDirty = normalizeComparable(form.email) !== normalizeComparable(originalForm.email);
  const isDocumentDirty =
    form.document.trim().replace(/\s+/g, '') !== originalForm.document.trim().replace(/\s+/g, '');

  const isFormDirty = useMemo(() => {
    return (Object.keys(originalForm) as Array<keyof EditUserFormState>).some((key) => {
      if (key === 'syncPasswordWithDocument') return false;
      if (key === 'units') {
        return unitsSignature(form.units) !== unitsSignature(originalForm.units);
      }
      if (key === 'assignedComplexIds') {
        return [...form.assignedComplexIds].sort().join(',') !== [...originalForm.assignedComplexIds].sort().join(',');
      }
      return form[key] !== originalForm[key];
    });
  }, [form, originalForm]);

  const showPasswordSync = isResidentialRole && isDocumentDirty && form.document.trim().length > 0;
  const unitsValidationError = isResidentialRole
    ? validateApartmentUnits(form.units, { required: form.role === 'owner' })
    : null;

  const canSave =
    isFormDirty &&
    !emailFormatError &&
    !unitsValidationError &&
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    !(form.role === 'manager' && !form.managedComplexId) &&
    !(form.role === 'builder' && form.assignedComplexIds.length === 0) &&
    !saving;

  const handleRoleChange = (nextRole: string) => {
    setForm((current) => ({
      ...current,
      role: nextRole,
      managedComplexId: nextRole === 'manager' ? current.managedComplexId : '',
      assignedComplexIds: nextRole === 'builder' ? current.assignedComplexIds : [],
      syncPasswordWithDocument: nextRole === 'manager' ? false : current.syncPasswordWithDocument,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;

    if (form.role === 'manager' && !form.managedComplexId) {
      notifyError('Debes asignar un conjunto al rol manager');
      return;
    }

    if (form.role === 'builder' && form.assignedComplexIds.length === 0) {
      notifyError('Debes asignar al menos un conjunto al rol constructora');
      return;
    }

    if (isResidentialRole && unitsValidationError) {
      notifyError(unitsValidationError);
      return;
    }

    if (showPasswordSync && form.syncPasswordWithDocument) {
      const shouldSync = await confirm({
        title: 'Sincronizar contraseña con documento',
        message:
          'La contraseña del usuario se reemplazará por el nuevo documento. Deberá usar ese valor para iniciar sesión.',
        confirmText: 'Sincronizar y guardar',
        cancelText: 'Cancelar',
        variant: 'warning',
      });
      if (!shouldSync) return;
    }

    setSaving(true);
    try {
      const apartments = isResidentialRole ? serializeApartmentUnits(form.units) : [];
      const response = await api.api.put(`/users/${user.id}`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        isActive: form.isActive,
        managedComplexId: form.role === 'manager' ? form.managedComplexId || undefined : undefined,
        assignedComplexIds: form.role === 'builder' ? form.assignedComplexIds : undefined,
        syncPasswordWithDocument: showPasswordSync && form.syncPasswordWithDocument,
        profile:
          form.role === 'manager' || form.role === 'builder'
            ? undefined
            : {
                apartments,
                apartment: apartments.map((unit) => unit.apartment).join(', '),
                complex: form.complex,
                complexId: form.complexId || undefined,
                coefficient: apartments.reduce((sum, unit) => sum + Number(unit.coefficient), 0),
                phone: form.phone?.trim() ? form.phone.trim() : undefined,
                documentNumber: form.document.trim() || undefined,
              },
      });

      const transfers = response.data?.transfers as
        | Array<{ apartment: string; fromEmail: string; coefficient: number }>
        | undefined;
      const changedIdentity = isEmailDirty || isDocumentDirty;

      if (transfers && transfers.length > 0) {
        const summary = transfers
          .map((item) => `${item.apartment} ← ${item.fromEmail}`)
          .join('; ');
        notifySuccess(
          `${transfers.length} unidad${transfers.length === 1 ? '' : 'es'} reasignada${transfers.length === 1 ? '' : 's'}; coeficientes actualizados. ${summary}`
        );
      } else {
        notifySuccess(
          changedIdentity
            ? 'Usuario actualizado (identidad y datos de acceso)'
            : 'Usuario actualizado correctamente'
        );
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error al actualizar usuario:', err);
      const details = err.response?.data?.errors;
      notifyError(
        Array.isArray(details) && details.length > 0
          ? details.join('; ')
          : err.response?.data?.message || 'Error al actualizar usuario'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">Editar usuario</h3>
              <p className="mt-1 truncate text-sm text-gray-500">
                {user.firstName} {user.lastName} · {user.email}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="space-y-5 overflow-y-auto px-5 py-4 text-sm text-gray-800">
            <section>
              <div className="mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                <UserRound className="h-4 w-4 text-blue-600" />
                <h4 className="font-semibold text-gray-900">Identidad</h4>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">Nombre</label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">Apellido</label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                  <Mail className="h-3.5 w-3.5" />
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500',
                    emailFormatError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                  )}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Se usa para iniciar sesión.</p>
                {emailFormatError ? (
                  <p className="mt-1 text-xs text-red-600">{emailFormatError}</p>
                ) : null}
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                <Shield className="h-4 w-4 text-blue-600" />
                <h4 className="font-semibold text-gray-900">Acceso</h4>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">Rol</label>
                  <select
                    value={form.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="builder">Constructora</option>
                    <option value="owner">Propietario</option>
                  </select>
                </div>
                <div className="flex items-end gap-2 pb-2">
                  <input
                    id="edit-user-is-active"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  <label htmlFor="edit-user-is-active" className="text-sm text-gray-700">
                    Usuario activo
                  </label>
                </div>
              </div>

              {form.role === 'manager' ? (
                <div className="mt-3">
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Conjunto administrado *
                  </label>
                  <select
                    value={form.managedComplexId}
                    onChange={(e) => setForm({ ...form, managedComplexId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar conjunto</option>
                    {complexes.map((complex) => (
                      <option key={complex.id} value={complex.id}>
                        {complex.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {form.role === 'builder' ? (
                <div className="mt-3">
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Conjuntos asignados *
                  </label>
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-300 p-2">
                    {complexes.map((complex) => {
                      const checked = form.assignedComplexIds.includes(complex.id);
                      return (
                        <label key={complex.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((current) => ({
                                ...current,
                                assignedComplexIds: checked
                                  ? current.assignedComplexIds.filter((id) => id !== complex.id)
                                  : [...current.assignedComplexIds, complex.id],
                              }));
                            }}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          {complex.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </section>

            {isResidentialRole ? (
              <section>
                <div className="mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                  <Hash className="h-4 w-4 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Datos residenciales</h4>
                </div>

                <div className="mb-3">
                  <label className="mb-1 block text-xs font-medium text-gray-600">Conjunto</label>
                  <select
                    value={form.complexId}
                    onChange={(e) => {
                      const selectedComplexId = e.target.value;
                      const selectedComplex = complexes.find((complex) => complex.id === selectedComplexId);
                      setForm({
                        ...form,
                        complexId: selectedComplexId,
                        complex: selectedComplex?.name || '',
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar conjunto</option>
                    {complexes.map((complex) => (
                      <option key={complex.id} value={complex.id}>
                        {complex.name}
                      </option>
                    ))}
                  </select>
                </div>

                <ApartmentUnitsEditor
                  units={form.units}
                  onChange={(units) => setForm({ ...form, units })}
                  showTower
                />
                {unitsValidationError ? (
                  <p className="mt-2 text-xs text-red-600">{unitsValidationError}</p>
                ) : null}

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Teléfono</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Documento</label>
                    <input
                      value={form.document}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          document: e.target.value,
                          syncPasswordWithDocument: false,
                        })
                      }
                      placeholder={originalForm.document || 'Número de documento'}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      También puede usarse como usuario de acceso. Mismas validaciones que la importación Excel.
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            {showPasswordSync ? (
              <section className="rounded-lg border border-amber-200 bg-amber-50/70 p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <KeyRound className="h-4 w-4" />
                  Seguridad
                </div>
                <label className="flex items-start gap-2 text-sm text-amber-950">
                  <input
                    type="checkbox"
                    checked={form.syncPasswordWithDocument}
                    onChange={(e) => setForm({ ...form, syncPasswordWithDocument: e.target.checked })}
                    className="mt-0.5 rounded border-amber-300 text-amber-700"
                  />
                  <span>
                    Usar el nuevo documento como contraseña
                    <span className="mt-1 block text-xs font-normal text-amber-800">
                      Si no marcas esta opción, la contraseña actual del usuario no cambia.
                    </span>
                  </span>
                </label>
              </section>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
            <p className="text-xs text-gray-500">
              {isFormDirty ? 'Tienes cambios sin guardar.' : 'Sin cambios pendientes.'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canSave}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader className="h-4 w-4 animate-spin" /> : null}
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
